import moment from "moment-timezone";
import { useMemo, useRef } from "react";
import { useTranslation } from "react-i18next";
import { Tooltip } from "reactstrap";
import { isUserActive, renderUserName } from "../../../../../utils/helpers/user";
import { useVisible } from "../../../../../utils/hooks/useVisible";
import { faTriangleExclamation, faUserAlt, faCheckSquare, faClockRotateLeft } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { usePreferences } from "../../../../Settings/Configuration/hooks/usePreferences";
import { useCompanyDateFormat } from "../../../../../utils/hooks/company";

function Column({ user, viewProfile }) {
    const { t } = useTranslation();
    const ref = useRef();
    const { visible, toggle } = useVisible();
    const isTerminated = user.status === "terminated";
    const isUserBeforeHiring = !isUserActive(user);
    const dateFormat = useCompanyDateFormat();
    const hideRetroAdjustment = usePreferences("timeSheetGeneral.hideRetroAdjustment");

    return (
        <>
            <span ref={ref} className={`${isTerminated ? 'terminated' : (isUserBeforeHiring ? 'hiring' : '')}`} >
                {viewProfile && (
                    <FontAwesomeIcon
                        icon={faUserAlt}
                        className="text-muted mr-2 cursor-pointer"
                        onClick={() => viewProfile(user)}
                    />
                )}
                {user.name || renderUserName(user, false)}
                {isTerminated && (
                    <>
                        &nbsp; <FontAwesomeIcon icon={faTriangleExclamation} />
                    </>
                )}
                {!isTerminated && isUserBeforeHiring && (
                    <>
                        &nbsp; <FontAwesomeIcon icon={faCheckSquare} />
                    </>
                )}
                {(user.hasRetro && !hideRetroAdjustment) && (
                    <>
                        &nbsp; <FontAwesomeIcon className="text-orange" icon={faClockRotateLeft} />
                    </>
                )}
            </span>

            {!isTerminated && isUserBeforeHiring && (
                <Tooltip isOpen={visible} toggle={toggle} target={ref} placement="auto">
                    <span className="text-sm font-weight-bolder px-2">
                        {t("user-hiring-at")}{" "}
                        {moment.parseZone(user.hiringDate).format("MMM DD YYYY")}
                    </span>
                </Tooltip>
            )}

            {isTerminated && (
                <Tooltip isOpen={visible} toggle={toggle} target={ref} placement="auto">
                    <span className="text-sm font-weight-bolder px-2">
                        {t("user-terminated-on")}{" "}
                        {moment.parseZone(user.terminatedAt).format(dateFormat)}
                    </span>
                </Tooltip>
            )}

            {(user.hasRetro && !hideRetroAdjustment) && (
                <Tooltip
                    isOpen={visible}
                    toggle={toggle}
                    target={ref}
                    placement="left-end"
                    delay={0}
                    fade={false}
                >
                    <span className="text-sm font-weight-bolder px-2">
                        {t("user-has-retro")}
                    </span>
                </Tooltip>
            )}
        </>
    );
}

export const useNameColumn = ({ viewProfile }) => {
    const { t } = useTranslation();

    return useMemo(() => ({
        Header: t("name"),
        id: "name",
        Footer: t("total"),
        Cell: ({ row: { original } }) => <Column user={original} viewProfile={viewProfile} />,
        canSort: true
    }), [t, viewProfile]);
};
